import * as Sentry from '@sentry/browser'
import type { AxiosError, InternalAxiosRequestConfig } from 'axios'
import type { BackendError } from 'src/types/errors'
import { ApiError } from 'src/errors/ApiError'
import { storeToRefs } from 'pinia'
import { authApi } from 'src/api'
import { api } from 'src/boot/axios'

const defaultError = (status: number | null, detail: string): BackendError => {
  return {
    type: 'client_error',
    errors: [{
      code: null,
      detail,
      attr: 'unexpected',
    }],
    status: status || null,
  }
}

type InternalAxiosRequestConfigWithRetry = InternalAxiosRequestConfig & {_retry?: boolean}

// If the backend generated an error, we throw it. If not, throw axios error.
// This is a server error for the user, not the developer
export const responseErrorInterceptor = async (axiosError: AxiosError) => {
  const auth = await import('stores/auth')
  const authStore = auth.useAuthStore()
  const originalRequest: InternalAxiosRequestConfigWithRetry | undefined = axiosError.config

  const error: BackendError = axiosError.response?.data as BackendError ||
    defaultError(axiosError.response?.status || null, axiosError.message)

  if (axiosError.response?.status === 401 && originalRequest && !originalRequest._retry) {
    const { refreshToken } = storeToRefs(authStore)
    if (!refreshToken.value) {
      authStore.logout()
      const err = new ApiError(error, axiosError.response?.status)
      Sentry.captureException(err)
      throw err
    }

    originalRequest._retry = true
    try {
      const refreshResponse = await authApi.refreshToken(refreshToken.value)
      authStore.applyLogin(refreshResponse)
      return api(originalRequest)
    } catch (e) {
      Sentry.captureException(e)
      console.error(e)
      authStore.logout()
      throw new ApiError(error, axiosError.response?.status)
    }
  } else {
    if (axiosError.response?.status === 403) {
      authStore.logout()
    }

    const err = new ApiError(error, axiosError.response?.status)
    Sentry.captureException(err)
    throw err
  }
}
